import { supabaseFetch } from './core';
import { devLog } from '../log';
import { computeStreak } from '../streak';

// ============================================
// STATISTICS
// ============================================

/**
 * Calcola cicli completati consecutivi seguendo la programmazione
 * Un ciclo = completare tutti i workout del programma nell'ordine corretto
 * Tolleranza: gap massimo di 3 giorni tra allenamenti
 */
const calculateProgramAdherenceStreak = async (athleteId, programId) => {
  if (!programId) {
    return { cyclesCompleted: 0, adherencePercentage: 0 };
  }

  try {
    // 1. Get program workouts ordered by day_number (fetch separately to avoid nested order syntax error)
    const workouts = await supabaseFetch(
      `/workouts?program_id=eq.${programId}&select=id,day_number&order=day_number.asc`
    );

    if (!workouts || workouts.length === 0) {
      return { cyclesCompleted: 0, adherencePercentage: 0 };
    }

    const workoutSequence = workouts.map(w => ({ id: w.id, dayNumber: w.day_number }));
    const cycleLength = workoutSequence.length;

    if (cycleLength === 0) {
      return { cyclesCompleted: 0, adherencePercentage: 0 };
    }

    // 2. Get workout logs (ultimi 6 mesi)
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const workoutIds = workoutSequence.map(w => w.id);
    const logs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&workout_id=in.(${workoutIds.join(',')})&completed_at=gte.${sixMonthsAgo.toISOString()}&select=workout_id,completed_at&order=completed_at.desc`
    );

    if (!logs || logs.length === 0) {
      return { cyclesCompleted: 0, adherencePercentage: 0 };
    }

    // 3. Inverti array per leggere cronologicamente (dal più vecchio al più recente)
    const sortedLogs = [...logs].reverse();

    /*#__PURE__*/ devLog('🔍 [DEBUG CICLI] Dati iniziali:', {
      totalLogs: sortedLogs.length,
      cycleLength,
      workoutSequence: workoutSequence.map(w => `Day${w.dayNumber}`),
      logsDetail: sortedLogs.map(l => {
        const w = workoutSequence.find(w2 => w2.id === l.workout_id);
        return `${new Date(l.completed_at).toLocaleDateString()} - Day${w?.dayNumber}`;
      })
    });

    // 4. Logica FLESSIBILE: conta cicli completati (tutti i workout, anche con ripetizioni)
    let cyclesCompleted = 0;
    let completedDays = new Set(); // Traccia quali day_number sono stati completati
    let lastLogDate = null;
    const MAX_GAP_DAYS = 14; // Tolleranza 2 settimane per maggiore flessibilità

    for (const log of sortedLogs) {
      const logDate = new Date(log.completed_at);
      const workout = workoutSequence.find(w => w.id === log.workout_id);

      if (!workout) continue;

      // Verifica gap troppo lungo (resetta tutto)
      if (lastLogDate) {
        const daysDiff = Math.floor((logDate - lastLogDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > MAX_GAP_DAYS) {
          // Gap troppo grande, resetta conteggio
          cyclesCompleted = 0;
          completedDays.clear();
        }
      }

      // Aggiungi questo day_number ai completati
      completedDays.add(workout.dayNumber);

      /*#__PURE__*/ devLog('🔍 [DEBUG CICLI] Step ', sortedLogs.indexOf(log) + 1, ':', {
        data: logDate.toLocaleDateString(),
        dayNumber: workout.dayNumber,
        completedDays: Array.from(completedDays),
        cycleComplete: completedDays.size === cycleLength
      });

      // Verifica se hai completato tutti i workout del ciclo
      if (completedDays.size === cycleLength) {
        // Ciclo completato! 🎉
        cyclesCompleted++;
        /*#__PURE__*/ devLog('🎉 [DEBUG CICLI] CICLO COMPLETATO! Totale cicli:', cyclesCompleted);
        completedDays.clear(); // Reset per il prossimo ciclo
      }

      lastLogDate = logDate;
    }

    // Se hai workout in corso ma non hai completato il ciclo, conta il progresso
    const currentDayInCycle = completedDays.size;

    // 5. Calcola percentuale aderenza
    const totalWorkoutsAnalyzed = logs.length;
    const expectedWorkouts = cyclesCompleted * cycleLength + currentDayInCycle;
    const adherencePercentage = expectedWorkouts > 0
      ? Math.round((expectedWorkouts / totalWorkoutsAnalyzed) * 100)
      : 0;

    /*#__PURE__*/ devLog('✅ [STREAK CICLI FLESSIBILE] Risultato:', {
      cyclesCompleted,
      currentDayInCycle,
      adherencePercentage,
      totalLogsAnalyzed: logs.length,
      completedDaysInCurrentCycle: Array.from(completedDays),
      programId
    });

    return {
      cyclesCompleted,
      currentDayInCycle,
      adherencePercentage,
      lastWorkoutDate: logs[0]?.completed_at
    };
  } catch (error) {
    console.error('❌ [DATABASE] calculateProgramAdherenceStreak error:', error);
    return { cyclesCompleted: 0, adherencePercentage: 0 };
  }
};

/**
 * Streak FRESCA per la card condivisibile: una sola query leggera (solo la
 * colonna completed_at, max 365 righe) eseguita on-demand DOPO il salvataggio
 * del workout, quindi include sempre l'allenamento appena completato.
 * Scelta al posto della cache dashboard (invalidata/stantia in quel momento)
 * e senza appesantire il flusso di completamento: si chiama solo quando
 * l'utente apre lo sheet di condivisione.
 */
export const getAthleteStreak = async (athleteId) => {
  const logs = await supabaseFetch(
    `/workout_logs?athlete_id=eq.${athleteId}&select=completed_at&order=completed_at.desc&limit=365`
  );
  return computeStreak((logs || []).map((l) => l.completed_at));
};

export const getAthleteStats = async (athleteId) => {
  /*#__PURE__*/ devLog('💾 [DATABASE] getAthleteStats called for athleteId:', athleteId);

  try {
    // Conta allenamenti totali (fetch non supporta count, prendiamo tutti e contiamo)
    const allLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&select=id,completed_at`
    );
    const totalWorkouts = allLogs ? allLogs.length : 0;

    // Conta allenamenti questo mese
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const monthLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&completed_at=gte.${startOfMonth.toISOString()}&select=id`
    );
    const monthWorkouts = monthLogs ? monthLogs.length : 0;

    // Calcola streak (giorni consecutivi). Limite 365: con 30 una streak
    // oltre il mese veniva troncata (payload comunque minimo: solo la data).
    const recentLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&select=completed_at&order=completed_at.desc&limit=365`
    );

    // Streak in ora locale; se oggi non c'è ancora un workout si parte da ieri
    // (una streak valida fino a ieri non deve azzerarsi al mattino).
    const streak = computeStreak((recentLogs || []).map(log => log.completed_at));

    // Get athlete's active program for cycle tracking
    let programCycles = { cyclesCompleted: 0, adherencePercentage: 0 };
    const activeProgram = await supabaseFetch(
      `/athlete_programs?athlete_id=eq.${athleteId}&is_active=eq.true&select=program_id&limit=1`
    );

    if (activeProgram && activeProgram.length > 0) {
      programCycles = await calculateProgramAdherenceStreak(athleteId, activeProgram[0].program_id);
    }

    /*#__PURE__*/ devLog('✅ [DATABASE] getAthleteStats successful:', { totalWorkouts, monthWorkouts, streak, cyclesCompleted: programCycles.cyclesCompleted });
    return {
      totalWorkouts,
      monthWorkouts,
      streak,
      cyclesCompleted: programCycles.cyclesCompleted, // ← NUOVO
      adherencePercentage: programCycles.adherencePercentage // ← NUOVO
    };
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteStats error:', error);
    return {
      totalWorkouts: 0,
      monthWorkouts: 0,
      streak: 0
    };
  }
};

// ============================================
// MUSCLE GROUP VOLUME TRACKING
// ============================================

/**
 * Calculate athlete's weekly volume by muscle group
 * @param {string} athleteId - Athlete UUID
 * @param {Date} weekStart - Start of week (Monday), defaults to current week
 * @returns {Object} - { muscleGroup: totalSets }
 */
export const getAthleteWeeklyVolume = async (athleteId, weekStart = null) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getAthleteWeeklyVolume called for athlete:', athleteId);

    // Default to current week (Monday-Sunday)
    if (!weekStart) {
      weekStart = new Date();
      weekStart.setHours(0, 0, 0, 0);
      // Get Monday of current week
      const day = weekStart.getDay();
      const diff = day === 0 ? -6 : 1 - day; // Sunday = 0, Monday = 1
      weekStart.setDate(weekStart.getDate() + diff);
    }

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    /*#__PURE__*/ devLog('📅 [DATABASE] Week range:', weekStart.toISOString(), 'to', weekEnd.toISOString());

    // 1. Get all workout logs for the week
    const workoutLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&completed_at=gte.${weekStart.toISOString()}&completed_at=lt.${weekEnd.toISOString()}&select=id`
    );

    if (!workoutLogs || workoutLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workouts found for this week');
      return {};
    }

    // 2. Get all exercise logs for these workouts (exclude excluded sets)
    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');
    const allExerciseLogs = await supabaseFetch(
      `/exercise_logs?workout_log_id=in.(${workoutLogIds})&select=exercise_id,exercise_template_id,set_number,excluded`
    );

    if (!allExerciseLogs || allExerciseLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercise logs found');
      return {};
    }

    // Filter out excluded sets
    const exerciseLogs = allExerciseLogs.filter(log => !log.excluded);

    if (exerciseLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No non-excluded exercise logs found');
      return {};
    }

    /*#__PURE__*/ devLog('📊 [DATABASE] Counting ', exerciseLogs.length, ' non-excluded sets (', allExerciseLogs.length, ' total)');


    // 3. Get unique exercise IDs and fetch their templates (include name for fallback)
    const validExerciseIds = [...new Set(exerciseLogs.map(el => el.exercise_id).filter(Boolean))];

    let exercises = [];
    if (validExerciseIds.length > 0) {
      exercises = await supabaseFetch(
        `/exercises?id=in.(${validExerciseIds.join(',')})&select=id,name,exercise_template_id`
      ) || [];
    }

    // Create exercise name map for fallback
    const exerciseNameMap = {};
    exercises.forEach(ex => { exerciseNameMap[ex.id] = ex.name; });

    // 4. Get templates with muscle groups
    const templateIdsFromExercises = exercises
      .filter(ex => ex.exercise_template_id)
      .map(ex => ex.exercise_template_id);

    const templateIdsFromFreeLogs = exerciseLogs
      .filter(el => !el.exercise_id && el.exercise_template_id)
      .map(el => el.exercise_template_id);

    const allTemplateIds = [...new Set([...templateIdsFromExercises, ...templateIdsFromFreeLogs])];

    let templates = [];
    let secondaryMuscles = [];

    if (allTemplateIds.length > 0) {
      const templateIds = allTemplateIds.join(',');
      templates = await supabaseFetch(
        `/exercise_templates?id=in.(${templateIds})&select=id,primary_muscle_group`
      ) || [];
      secondaryMuscles = await supabaseFetch(
        `/exercise_secondary_muscles?exercise_template_id=in.(${templateIds})&select=exercise_template_id,muscle_group,involvement_percentage`
      ) || [];
    }

    // Create exercise -> template map
    const exerciseTemplateMap = {};
    exercises.forEach(ex => {
      if (ex.exercise_template_id) {
        exerciseTemplateMap[ex.id] = ex.exercise_template_id;
      }
    });

    // Create template data map (primary + secondaries)
    const templateDataMap = {};
    templates.forEach(t => {
      templateDataMap[t.id] = {
        primaryMuscle: t.primary_muscle_group,
        secondaryMuscles: secondaryMuscles
          .filter(sm => sm.exercise_template_id === t.id)
          .map(sm => ({
            muscle: sm.muscle_group,
            percentage: sm.involvement_percentage
          }))
      };
    });

    // 5. Calculate volume
    const volumeByMuscle = {};

    exerciseLogs.forEach(log => {
      const templateId = log.exercise_id
        ? exerciseTemplateMap[log.exercise_id]
        : log.exercise_template_id;

      if (templateId && templateDataMap[templateId]) {
        const data = templateDataMap[templateId];

        if (data.primaryMuscle && data.primaryMuscle !== 'Cardio') {
          volumeByMuscle[data.primaryMuscle] = (volumeByMuscle[data.primaryMuscle] || 0) + 1.0;
        }

        data.secondaryMuscles.forEach(sm => {
          const contribution = sm.percentage / 100;
          volumeByMuscle[sm.muscle] = (volumeByMuscle[sm.muscle] || 0) + contribution;
        });
      } else {
        // Fallback for exercises without template mapping - always count as "Altro"
        volumeByMuscle['Altro'] = (volumeByMuscle['Altro'] || 0) + 1.0;
      }
    });

    /*#__PURE__*/ devLog('✅ [DATABASE] getAthleteWeeklyVolume successful:', volumeByMuscle);
    return volumeByMuscle;
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteWeeklyVolume error:', error);
    return {};
  }
};

/**
 * Detailed weekly volume with per-exercise breakdown
 * Returns { "Petto": { total: 8, exercises: [{ name: "Panca Piana", sets: 4 }] } }
 */
export const getAthleteWeeklyVolumeDetailed = async (athleteId, weekStartDate = null) => {
  try {
    let weekStart;
    if (weekStartDate) {
      weekStart = new Date(weekStartDate);
      weekStart.setHours(0, 0, 0, 0);
    } else {
      weekStart = new Date();
      weekStart.setHours(0, 0, 0, 0);
      const day = weekStart.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      weekStart.setDate(weekStart.getDate() + diff);
    }

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);

    /*#__PURE__*/ devLog('📊 [VOLUME] Week range: ', weekStart.toISOString(), ' → ', weekEnd.toISOString());

    // 1. Get workout logs for the week
    const workoutLogs = await supabaseFetch(
      `/workout_logs?athlete_id=eq.${athleteId}&completed_at=gte.${weekStart.toISOString()}&completed_at=lt.${weekEnd.toISOString()}&select=id`
    );

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 1: ', workoutLogs?.length || 0, ' workout_logs found');

    if (!workoutLogs || workoutLogs.length === 0) {
      return { weekStart: weekStart.toISOString(), weekEnd: weekEnd.toISOString(), muscles: {} };
    }

    // 2. Get exercise logs (exclude excluded sets)
    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');
    const allExerciseLogs = await supabaseFetch(
      `/exercise_logs?workout_log_id=in.(${workoutLogIds})&select=exercise_id,exercise_template_id,set_number,excluded`
    );

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 2: ', allExerciseLogs?.length || 0, ' exercise_logs found');

    if (!allExerciseLogs || allExerciseLogs.length === 0) {
      return { weekStart: weekStart.toISOString(), weekEnd: weekEnd.toISOString(), muscles: {} };
    }

    // Filter out excluded sets
    const exerciseLogs = allExerciseLogs.filter(log => !log.excluded);

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 2b: ', exerciseLogs.length, ' non-excluded sets (', allExerciseLogs.length, ' total)');

    if (exerciseLogs.length === 0) {
      return { weekStart: weekStart.toISOString(), weekEnd: weekEnd.toISOString(), muscles: {} };
    }

    // 3. Get exercise -> template mapping (include name for fallback)
    const validExerciseIds = [...new Set(exerciseLogs.map(el => el.exercise_id).filter(Boolean))];
    let exercises = [];
    if (validExerciseIds.length > 0) {
      exercises = await supabaseFetch(
        `/exercises?id=in.(${validExerciseIds.join(',')})&select=id,name,exercise_template_id`
      ) || [];
    }

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 3: ', validExerciseIds.length, ' exercise_ids → ', exercises.length, ' exercises found');

    // Create exercise name map for fallback
    const exerciseNameMap = {};
    exercises.forEach(ex => { exerciseNameMap[ex.id] = ex.name; });

    // 4. Get templates with names and muscle groups
    const templateIdsFromExercises = exercises.filter(ex => ex.exercise_template_id).map(ex => ex.exercise_template_id);
    const templateIdsFromFreeLogs = exerciseLogs.filter(el => !el.exercise_id && el.exercise_template_id).map(el => el.exercise_template_id);
    const allTemplateIds = [...new Set([...templateIdsFromExercises, ...templateIdsFromFreeLogs])];

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 4: templateIds from exercises: ', templateIdsFromExercises.length, ', from free logs: ', templateIdsFromFreeLogs.length, ', total unique: ', allTemplateIds.length);

    let templates = [];
    let secondaryMuscles = [];

    if (allTemplateIds.length > 0) {
      const templateIds = allTemplateIds.join(',');
      templates = await supabaseFetch(
        `/exercise_templates?id=in.(${templateIds})&select=id,name,primary_muscle_group`
      ) || [];
      secondaryMuscles = await supabaseFetch(
        `/exercise_secondary_muscles?exercise_template_id=in.(${templateIds})&select=exercise_template_id,muscle_group,involvement_percentage`
      ) || [];
    }

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 4b: ', templates.length, ' templates, ', secondaryMuscles.length, ' secondary muscles');

    // Maps
    const exerciseTemplateMap = {};
    exercises.forEach(ex => { if (ex.exercise_template_id) exerciseTemplateMap[ex.id] = ex.exercise_template_id; });

    const templateDataMap = {};
    templates.forEach(t => {
      templateDataMap[t.id] = {
        name: t.name,
        primaryMuscle: t.primary_muscle_group,
        secondaryMuscles: secondaryMuscles
          .filter(sm => sm.exercise_template_id === t.id)
          .map(sm => ({ muscle: sm.muscle_group, percentage: sm.involvement_percentage }))
      };
    });

    // 5. Calculate detailed volume
    const muscles = {};
    let matchedSets = 0;
    let unmatchedSets = 0;

    exerciseLogs.forEach(log => {
      const templateId = log.exercise_id ? exerciseTemplateMap[log.exercise_id] : log.exercise_template_id;

      if (templateId && templateDataMap[templateId]) {
        matchedSets++;
        const data = templateDataMap[templateId];
        const exerciseName = data.name || log.exercise_name || 'Sconosciuto';

        // Primary muscle
        if (data.primaryMuscle && data.primaryMuscle !== 'Cardio') {
          if (!muscles[data.primaryMuscle]) muscles[data.primaryMuscle] = { total: 0, exercises: {} };
          muscles[data.primaryMuscle].total += 1.0;
          muscles[data.primaryMuscle].exercises[exerciseName] = (muscles[data.primaryMuscle].exercises[exerciseName] || 0) + 1.0;
        }

        // Secondary muscles
        data.secondaryMuscles.forEach(sm => {
          const contribution = sm.percentage / 100;
          if (!muscles[sm.muscle]) muscles[sm.muscle] = { total: 0, exercises: {} };
          muscles[sm.muscle].total += contribution;
          muscles[sm.muscle].exercises[exerciseName] = (muscles[sm.muscle].exercises[exerciseName] || 0) + contribution;
        });
      } else {
        unmatchedSets++;
        // Fallback: count unmatched exercises as "Altro"
        // Try to get exercise name from: exercise_logs.exercise_name, exercises table, or default
        const exerciseName = log.exercise_name || (log.exercise_id && exerciseNameMap[log.exercise_id]) || 'Esercizio';
        const fallbackMuscle = 'Altro';
        if (!muscles[fallbackMuscle]) muscles[fallbackMuscle] = { total: 0, exercises: {} };
        muscles[fallbackMuscle].total += 1.0;
        muscles[fallbackMuscle].exercises[exerciseName] = (muscles[fallbackMuscle].exercises[exerciseName] || 0) + 1.0;
      }
    });

    /*#__PURE__*/ devLog('📊 [VOLUME] Step 5: ', matchedSets, ' matched, ', unmatchedSets, ' unmatched → ', Object.keys(muscles).length, ' muscle groups');

    return { weekStart: weekStart.toISOString(), weekEnd: weekEnd.toISOString(), muscles };
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteWeeklyVolumeDetailed error:', error);
    return { weekStart: '', weekEnd: '', muscles: {} };
  }
};

/**
 * Get athlete's muscle group volume history over multiple weeks
 * @param {string} athleteId - Athlete UUID
 * @param {number} numWeeks - Number of weeks to fetch (default 8)
 * @returns {Array} - [{ weekStart, weekEnd, volumeByMuscle }]
 */
export const getAthleteMuscleGroupHistory = async (athleteId, numWeeks = 8) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getAthleteMuscleGroupHistory for', numWeeks, 'weeks');

    const history = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < numWeeks; i++) {
      // Calculate Monday of week
      const weekStart = new Date(today);
      const day = weekStart.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      weekStart.setDate(weekStart.getDate() + diff - (i * 7));

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6); // Sunday

      const volumeByMuscle = await getAthleteWeeklyVolume(athleteId, weekStart);

      history.push({
        weekStart: weekStart.toISOString(),
        weekEnd: weekEnd.toISOString(),
        volumeByMuscle
      });
    }

    /*#__PURE__*/ devLog('✅ [DATABASE] getAthleteMuscleGroupHistory successful');
    return history.reverse(); // Oldest first
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteMuscleGroupHistory error:', error);
    return [];
  }
};

// ============================================
// EXERCISE PROGRESS & RECORDS
// ============================================

/**
 * Get all exercises performed by athlete with personal records
 * @param {string} athleteId - Athlete UUID
 * @returns {Array} - [{exerciseId, exerciseName, record: {weight, reps, date}, lastPerformed: {weight, reps, date}}]
 */
export const getAthleteExerciseRecords = async (athleteId) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getAthleteExerciseRecords for athlete:', athleteId);

    // 1. Get all workout logs for athlete
    const workoutLogs = await supabaseFetch(`/workout_logs?athlete_id=eq.${athleteId}&select=id,completed_at&order=completed_at.desc`);

    if (!workoutLogs || workoutLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workout logs found');
      return [];
    }

    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');

    // 2. Get all exercise logs (including cardio fields)
    const exerciseLogs = await supabaseFetch(`/exercise_logs?workout_log_id=in.(${workoutLogIds})&select=id,exercise_id,exercise_template_id,set_number,reps,weight,workout_log_id,duration_minutes,speed,incline,distance`);

    if (!exerciseLogs || exerciseLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercise logs found');
      return [];
    }

    // 3. Get exercise details (normal exercises)
    const normalLogs = exerciseLogs.filter(el => el.exercise_id !== null);
    const exerciseIds = [...new Set(normalLogs.map(el => el.exercise_id))];
    let exercises = [];
    if (exerciseIds.length > 0) {
      exercises = await supabaseFetch(`/exercises?id=in.(${exerciseIds.join(',')})&select=id,name`);
    }

    // 4. Get exercise templates (extra exercises)
    const extraLogs = exerciseLogs.filter(el => el.exercise_id === null && el.exercise_template_id !== null);
    const templateIds = [...new Set(extraLogs.map(el => el.exercise_template_id))];
    let templates = [];
    if (templateIds.length > 0) {
      templates = await supabaseFetch(`/exercise_templates?id=in.(${templateIds.join(',')})&select=id,name`);
    }

    // 5. Create map of workout_log_id -> completed_at
    const workoutDateMap = {};
    workoutLogs.forEach(wl => {
      workoutDateMap[wl.id] = wl.completed_at;
    });

    // 6. Group logs by exercise and calculate records
    const exerciseMap = {};

    exerciseLogs.forEach(log => {
      // For normal exercises use exercise_id, for extra use template_id
      const key = log.exercise_id !== null
        ? log.exercise_id
        : `template-${log.exercise_template_id}`;

      const completedAt = workoutDateMap[log.workout_log_id];

      if (!exerciseMap[key]) {
        // Find name from either exercises or templates
        let name = 'Sconosciuto';
        if (log.exercise_id !== null) {
          name = exercises.find(e => e.id === log.exercise_id)?.name || 'Sconosciuto';
        } else if (log.exercise_template_id !== null) {
          name = templates.find(t => t.id === log.exercise_template_id)?.name || 'Sconosciuto';
        }

        exerciseMap[key] = {
          exerciseId: key,
          exerciseName: name,
          allLogs: [],
          isCardio: false
        };
      }

      // Check if this is a cardio exercise (has duration_minutes)
      const isCardioLog = log.duration_minutes != null && log.duration_minutes > 0;

      if (isCardioLog) {
        exerciseMap[key].isCardio = true;
        exerciseMap[key].allLogs.push({
          duration: log.duration_minutes,
          distance: log.distance || 0,
          speed: log.speed || 0,
          incline: log.incline || 0,
          date: completedAt
        });
      } else if (log.weight && log.reps && log.reps >= 3) {
        // Only include strength logs with valid weight and reps >= 3
        exerciseMap[key].allLogs.push({
          weight: log.weight,
          reps: log.reps,
          date: completedAt
        });
      }
    });

    // 6. Calculate record and last performed for each exercise
    const results = Object.values(exerciseMap)
      .filter(ex => ex.allLogs.length > 0)
      .map(ex => {
        if (ex.isCardio) {
          // Cardio: sort by distance to find record, or by duration if no distance
          const sortedByDistance = [...ex.allLogs].sort((a, b) => (b.distance || 0) - (a.distance || 0));
          const sortedByDuration = [...ex.allLogs].sort((a, b) => (b.duration || 0) - (a.duration || 0));
          const bestDistance = sortedByDistance[0];
          const longestDuration = sortedByDuration[0];

          // Sort by date desc to find last performed
          const sortedByDate = [...ex.allLogs].sort((a, b) => new Date(b.date) - new Date(a.date));
          const lastPerformed = sortedByDate[0];

          return {
            exerciseId: ex.exerciseId,
            exerciseName: ex.exerciseName,
            isCardio: true,
            record: {
              distance: bestDistance.distance,
              duration: longestDuration.duration,
              speed: bestDistance.speed,
              date: bestDistance.distance > 0 ? bestDistance.date : longestDuration.date
            },
            lastPerformed: {
              duration: lastPerformed.duration,
              distance: lastPerformed.distance,
              speed: lastPerformed.speed,
              date: lastPerformed.date
            },
            totalSessions: new Set(ex.allLogs.map(l => l.date)).size
          };
        } else {
          // Strength: sort by weight desc to find record
          const sortedByWeight = [...ex.allLogs].sort((a, b) => b.weight - a.weight);
          const record = sortedByWeight[0];

          // Sort by date desc to find last performed
          const sortedByDate = [...ex.allLogs].sort((a, b) => new Date(b.date) - new Date(a.date));
          const lastPerformed = sortedByDate[0];

          return {
            exerciseId: ex.exerciseId,
            exerciseName: ex.exerciseName,
            isCardio: false,
            record: {
              weight: record.weight,
              reps: record.reps,
              date: record.date
            },
            lastPerformed: {
              weight: lastPerformed.weight,
              reps: lastPerformed.reps,
              date: lastPerformed.date
            },
            totalSessions: new Set(ex.allLogs.map(l => l.date)).size
          };
        }
      })
      // Sort by exercise name
      .sort((a, b) => a.exerciseName.localeCompare(b.exerciseName));

    /*#__PURE__*/ devLog('✅ [DATABASE] getAthleteExerciseRecords successful, found', results.length, 'exercises');
    return results;
  } catch (error) {
    console.error('❌ [DATABASE] getAthleteExerciseRecords error:', error);
    return [];
  }
};

/**
 * Get chronological history for a specific exercise
 * @param {string} athleteId - Athlete UUID
 * @param {string} exerciseId - Exercise UUID
 * @param {number} limit - Max number of sessions to return (default 20)
 * @returns {Array} - [{date, sets: [{setNumber, weight, reps, notes}]}]
 */
export const getExerciseProgressHistory = async (athleteId, exerciseId, limit = 20) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getExerciseProgressHistory for exercise:', exerciseId);

    // 1. Get all workout logs for athlete (sorted by date desc)
    const workoutLogs = await supabaseFetch(`/workout_logs?athlete_id=eq.${athleteId}&select=id,completed_at,workout_id&order=completed_at.desc&limit=100`);

    if (!workoutLogs || workoutLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No workout logs found');
      return [];
    }

    const workoutLogIds = workoutLogs.map(wl => wl.id).join(',');

    // 2. Get all exercise logs for this specific exercise (including cardio fields)
    // Handle both normal exercises (exercise_id) and extra exercises (template-{id})
    let exerciseLogs;
    if (exerciseId.startsWith('template-')) {
      // Extra exercise - filter by exercise_template_id
      const templateId = exerciseId.replace('template-', '');
      exerciseLogs = await supabaseFetch(`/exercise_logs?workout_log_id=in.(${workoutLogIds})&exercise_template_id=eq.${templateId}&select=id,set_number,reps,weight,notes,excluded,workout_log_id,duration_minutes,speed,incline,distance&order=set_number.asc`);
    } else {
      // Normal exercise - filter by exercise_id
      exerciseLogs = await supabaseFetch(`/exercise_logs?workout_log_id=in.(${workoutLogIds})&exercise_id=eq.${exerciseId}&select=id,set_number,reps,weight,notes,excluded,workout_log_id,duration_minutes,speed,incline,distance&order=set_number.asc`);

      // FALLBACK: If no data found, try with exercise_template_id
      if (!exerciseLogs || exerciseLogs.length === 0) {
        // Get exercise to find its template_id
        const exerciseData = await supabaseFetch(`/exercises?id=eq.${exerciseId}&select=exercise_template_id`);
        if (exerciseData && exerciseData[0]?.exercise_template_id) {
          const templateId = exerciseData[0].exercise_template_id;
          /*#__PURE__*/ devLog('⚠️ No logs found for exercise_id, trying template_id:', templateId);
          exerciseLogs = await supabaseFetch(`/exercise_logs?workout_log_id=in.(${workoutLogIds})&exercise_template_id=eq.${templateId}&select=id,set_number,reps,weight,notes,excluded,workout_log_id,duration_minutes,speed,incline,distance&order=set_number.asc`);
        }
      }
    }

    if (!exerciseLogs || exerciseLogs.length === 0) {
      /*#__PURE__*/ devLog('⚠️ [DATABASE] No exercise logs found for this exercise');
      return [];
    }

    // 3. Group by workout_log_id
    const groupedByWorkout = {};
    exerciseLogs.forEach(log => {
      const workoutLogId = log.workout_log_id;
      if (!groupedByWorkout[workoutLogId]) {
        const workout = workoutLogs.find(wl => wl.id === workoutLogId);
        groupedByWorkout[workoutLogId] = {
          date: workout.completed_at,
          workoutId: workout.workout_id,
          workoutLogId: workoutLogId,
          sets: []
        };
      }

      // Check if this is a cardio log
      const isCardioLog = log.duration_minutes != null && log.duration_minutes > 0;
      if (isCardioLog) {
        groupedByWorkout[workoutLogId].isCardio = true;
      }

      groupedByWorkout[workoutLogId].sets.push({
        id: log.id,
        setNumber: log.set_number,
        weight: log.weight,
        reps: log.reps,
        notes: log.notes,
        excluded: log.excluded || false,
        // Cardio fields
        duration: log.duration_minutes,
        distance: log.distance,
        speed: log.speed,
        incline: log.incline
      });
    });

    // 4. Convert to array, compute session-level excluded, sort by date desc, then limit
    const history = Object.values(groupedByWorkout)
      .map(session => ({
        ...session,
        isCardio: session.isCardio || false,
        excluded: session.sets.length > 0 && session.sets.every(s => s.excluded)
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, limit);

    /*#__PURE__*/ devLog('✅ [DATABASE] getExerciseProgressHistory successful, found', history.length, 'sessions');
    return history;
  } catch (error) {
    console.error('❌ [DATABASE] getExerciseProgressHistory error:', error);
    return [];
  }
};

/**
 * Get exercise data formatted for charts (Recharts)
 * Returns chronological data with max weight and avg reps per session
 * @param {string} athleteId - Athlete UUID
 * @param {string} exerciseId - Exercise UUID (can be 'template-{id}' for extra exercises)
 * @param {number} limit - Max number of sessions (default 20)
 * @returns {Array} - [{ date, dateLabel, maxWeight, avgReps, totalSets }]
 */
export const getExerciseChartData = async (athleteId, exerciseId, limit = 20) => {
  try {
    /*#__PURE__*/ devLog('💾 [DATABASE] getExerciseChartData for exercise:', exerciseId);

    // Use the existing getExerciseProgressHistory function
    const history = await getExerciseProgressHistory(athleteId, exerciseId, limit);

    if (!history || history.length === 0) {
      return [];
    }

    // Filter out excluded sessions for chart data
    const includedHistory = history.filter(session => !session.excluded);

    if (includedHistory.length === 0) {
      return [];
    }

    // Check if this is cardio (first session determines type)
    const isCardio = includedHistory[0]?.isCardio || false;

    // Transform to chart-friendly format
    const chartData = includedHistory.map(session => {
      const date = new Date(session.date);
      const dateLabel = date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });

      if (isCardio) {
        // Cardio: aggregate duration and distance
        const durations = session.sets.map(s => s.duration).filter(Boolean);
        const distances = session.sets.map(s => s.distance).filter(Boolean);
        const speeds = session.sets.map(s => s.speed).filter(Boolean);

        const totalDuration = durations.reduce((a, b) => a + b, 0);
        const totalDistance = distances.reduce((a, b) => a + b, 0);
        const avgSpeed = speeds.length > 0 ? Math.round(speeds.reduce((a, b) => a + b, 0) / speeds.length * 10) / 10 : 0;

        return {
          date: session.date,
          dateLabel,
          isCardio: true,
          duration: totalDuration,
          distance: totalDistance,
          avgSpeed,
          totalSets: session.sets.length
        };
      } else {
        // Strength: aggregate weights and reps
        const weights = session.sets.map(s => s.weight).filter(Boolean);
        const reps = session.sets.map(s => s.reps).filter(Boolean);

        const maxWeight = weights.length > 0 ? Math.max(...weights) : 0;
        const avgReps = reps.length > 0 ? Math.round(reps.reduce((a, b) => a + b, 0) / reps.length) : 0;

        return {
          date: session.date,
          dateLabel,
          isCardio: false,
          maxWeight,
          avgReps,
          totalSets: session.sets.length
        };
      }
    });

    // Reverse so oldest is first (for chart display left-to-right)
    const result = chartData.reverse();

    /*#__PURE__*/ devLog('✅ [DATABASE] getExerciseChartData successful, returned', result.length, 'data points');
    return result;
  } catch (error) {
    console.error('❌ [DATABASE] getExerciseChartData error:', error);
    return [];
  }
};
